import { FC, useState, ChangeEvent, useMemo, useCallback, useEffect, MouseEvent, useRef } from 'react';
import { Button, Loading, Modal, Table, Tag } from '../../../../components';
import { RegistrationPage } from '../../templates';
import { RegistrationEntities, TagActiveLabel, useDebounce } from '../../../../utils';
import {
	AdminUsuarioLista,
	AdminUsuarioTotais,
	deleteAdminUser,
	getAdminUsersList,
	getAdminUsersTotals,
} from '../../../../services';
import { ActionsContainer, Dashboard, TableWrapper, TagsContainer } from '../../Registration.styles';
import { Tooltip } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { useToastStore } from '../../../../stores';
import DashboardCard, { DashboardStatus } from '../../../../components/dashboard-card/DashboardCard';

const emptyTotals: AdminUsuarioTotais = {
	qtd_ativo: 0,
	qtd_inativo: 0,
	qtd_recusado: 0,
};

const statusFromCard = (card: DashboardStatus | null) => {
	if (card === DashboardStatus.Active) return 'ativo';
	if (card === DashboardStatus.Inactive) return 'inativo';
	if (card === DashboardStatus.Recused) return 'recusado';
	return undefined;
};

const revendaIdFromFilters = (filters?: string) => {
	if (!filters?.startsWith('revenda-')) return undefined;
	const id = filters.slice('revenda-'.length);
	return /^\d+$/.test(id) ? id : undefined;
};

const RegistrationUsers: FC = () => {
	const { setToast } = useToastStore();
	const { filters } = useParams();
	const idRevenda = revendaIdFromFilters(filters);
	const [loading, setLoading] = useState<boolean>(true);
	const [tableLoading, setTableLoading] = useState<boolean>(false);
	const [users, setUsers] = useState<AdminUsuarioLista[]>([]);
	const [totals, setTotals] = useState<AdminUsuarioTotais>(emptyTotals);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [userToDelete, setUserToDelete] = useState<number>();
	const [activeCard, setActiveCard] = useState<DashboardStatus | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearch = useDebounce(searchTerm, 500);
	const initialLoad = useRef(true);

	const tableColumns = useMemo(
		() => [
			{ id: 'name', label: 'Nome' },
			{ id: 'email', label: 'E-mail' },
			{ id: 'role', label: 'Perfil' },
			{ id: 'status', label: 'Status' },
			{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const defineActiveLabel = useCallback((status: string) => {
		if (status === TagActiveLabel.Active) return TagActiveLabel.Active;
		if (status === TagActiveLabel.Inactive) return TagActiveLabel.Inactive;
		return null;
	}, []);

	const handleDeleteConfirm = useCallback((id: number) => {
		setOpenDeleteModal(true);
		setUserToDelete(id);
	}, []);

	const rowMapper = useCallback(
		(item: AdminUsuarioLista) => {
			const label = defineActiveLabel(item.status);

			return {
				name: { value: item.nome, rawValue: item.nome },
				email: { value: item.email, rawValue: item.email },
				role: { value: item.perfil, rawValue: item.perfil },
				status: {
					value: label ? (
						<TagsContainer>
							<Tag status={label} />
						</TagsContainer>
					) : (
						item.status
					),
					rawValue: item.status,
				},
				actions: {
					value: (
						<ActionsContainer>
							<Tooltip title='Editar' placement='top' arrow>
								<Link to={`/cadastros/usuarios/editar/${item.id}`}>
									<Button variant='icon' icon={<EditOutlined fontSize='inherit' />} />
								</Link>
							</Tooltip>
							<Tooltip title='Desativar' placement='top' arrow>
								<div>
									<Button
										variant='icon'
										icon={<DeleteOutlined fontSize='inherit' />}
										onClick={() => handleDeleteConfirm(item.id)}
									/>
								</div>
							</Tooltip>
						</ActionsContainer>
					),
				},
			};
		},
		[handleDeleteConfirm, defineActiveLabel]
	);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const handleClearSearch = (_e: MouseEvent<HTMLButtonElement>) => {
		setSearchTerm('');
	};

	const handleFilterChange = (status: DashboardStatus) => {
		if (status === activeCard) {
			setActiveCard(null);
			return;
		}

		setActiveCard(status);
	};

	const loadUsers = useCallback(async () => {
		const list = await getAdminUsersList(debouncedSearch, statusFromCard(activeCard), idRevenda);
		setUsers(list);
	}, [debouncedSearch, activeCard, idRevenda]);

	const loadTotals = useCallback(async () => {
		const totais = await getAdminUsersTotals();
		setTotals(totais ?? emptyTotals);
	}, []);

	const handleDelete = async () => {
		try {
			const response = await deleteAdminUser({ id: userToDelete!.toString() });

			if (!response.includes('sucesso')) {
				setToast({ message: response, variant: 'error' });
			} else {
				setToast({
					message: 'Usuário removido com sucesso!',
					variant: 'success',
				});
				await Promise.all([loadUsers(), loadTotals()]);
			}
		} catch {
			setToast({
				message: 'Erro ao remover o Usuário!',
				variant: 'error',
			});
		} finally {
			setOpenDeleteModal(false);
		}
	};

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={users?.map(rowMapper)} paginate />,
		[users, rowMapper, tableColumns]
	);

	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			const first = initialLoad.current;
			if (first) setLoading(true);
			else setTableLoading(true);

			try {
				const listPromise = getAdminUsersList(debouncedSearch, statusFromCard(activeCard), idRevenda);
				const totalsPromise = first ? getAdminUsersTotals() : Promise.resolve(null);
				const [list, totais] = await Promise.all([listPromise, totalsPromise]);

				if (cancelled) return;

				setUsers(list ?? []);
				if (totais) setTotals(totais);
			} catch {
				if (!cancelled) {
					setToast({
						message: 'Erro ao buscar os usuários!',
						variant: 'error',
					});
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
					setTableLoading(false);
					initialLoad.current = false;
				}
			}
		};

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, activeCard, idRevenda, setToast]);

	if (loading) return <Loading fullScreen />;

	return (
		<>
			<Dashboard>
				<DashboardCard
					active={activeCard === DashboardStatus.Active}
					variant={DashboardStatus.Active}
					amount={totals.qtd_ativo}
					onClick={() => handleFilterChange(DashboardStatus.Active)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Inactive}
					variant={DashboardStatus.Inactive}
					amount={totals.qtd_inativo}
					onClick={() => handleFilterChange(DashboardStatus.Inactive)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Recused}
					variant={DashboardStatus.Recused}
					amount={totals.qtd_recusado}
					onClick={() => handleFilterChange(DashboardStatus.Recused)}
				/>
			</Dashboard>
			<RegistrationPage
				entity={RegistrationEntities.user}
				searchTerm={searchTerm}
				onChange={handleSearchChange}
				onClear={handleClearSearch}
			>
				<>
					<Modal
						open={openDeleteModal}
						onClose={() => setOpenDeleteModal(false)}
						title='Confirmação!'
						primaryButtonText='Sim'
						secondaryButtonText='Não'
						onPrimaryButtonClick={handleDelete}
						onSecondaryButtonClick={() => setOpenDeleteModal(false)}
					>
						Deseja realmente desativar?
					</Modal>
					<TableWrapper>{tableLoading ? <Loading /> : MemoizedTable}</TableWrapper>
				</>
			</RegistrationPage>
		</>
	);
};

export default RegistrationUsers;
